import React, { Component } from "react";
import "./EnergyConsumeReport.scss";
import InMultiSelect from "../InMultiSelect/InMultiSelect";
import DatePicker from "react-datepicker";
import moment from "moment";
import ReportAPIs from "../../../../APIs/ReportAPIs";
import { JSONToCSVConvertor } from "../../../../helpers/helperFunctions";

const formatStatus = (status) => {
  if (!status) return "Unknown";
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusTooltip = (status) => {
  const key = String(status || "")
    .toLowerCase()
    .replace(/[\s_]/g, "");
  const tips = {
    inprogress: "Energy calculation is still in progress for this day",
    completed: "Energy calculation is completed for this day",
    complete: "Energy calculation is completed for this day",
  };
  if (!status) return "Status is not available for this day";
  return tips[key] || `Status: ${formatStatus(status)}`;
};

const formatDuration = (start, end) => {
  if (start == null || end == null) return "--";
  const mins = Math.max(0, Math.round((end - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const vehicleHasData = (vehicle) =>
  (vehicle.days || []).some((day) => day.totalenergyconsumed != null);

const sortVehiclesByData = (vehicles) =>
  [...vehicles].sort((a, b) => {
    const aHas = vehicleHasData(a);
    const bHas = vehicleHasData(b);
    if (aHas !== bHas) return aHas ? -1 : 1;
    return String(a.vehicleno).localeCompare(String(b.vehicleno));
  });

class EnergyConsumeReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDay: moment().startOf("day").toDate(),
      busy: false,
      vehicles: [],
      expandedVehicleId: null,
      expandedDay: {},
      errorMsg: null,
      downloadMenuOpen: false,
    };
    this.unmounted = false;
    this.downloadWrapRef = React.createRef();
  }

  changeVehicle = (v) => {
    window.saveVehicleState(v);
    if (this.vehicleSelect) this.vehicleSelect.setFocusActive();
  };

  handleDayChange = (date) => {
    this.setState({ selectedDay: date });
  };

  getSelectedVehicles = () => {
    return window.selectedItems && window.selectedItems.length
      ? window.selectedItems
      : window.selectedVehicles || [];
  };

  resolveVehicleName = (selected, dayList) => {
    const first = (dayList && dayList[0]) || {};
    if (first.vehicleno) return first.vehicleno;
    if (selected.name) return selected.name;
    if (selected.data && selected.data.vehicleno) return selected.data.vehicleno;
    const map = window.vehicleMap || {};
    const fromMap = map[selected.id] || map[String(selected.id)];
    return (fromMap && fromMap.vehicleno) || String(selected.id);
  };

  getReport = () => {
    const selected = this.getSelectedVehicles();
    if (!selected.length) {
      this.setState({
        busy: false,
        errorMsg: "Please select a vehicle",
        vehicles: [],
        expandedVehicleId: null,
        expandedDay: {},
      });
      return;
    }

    const starttime = moment(this.state.selectedDay).startOf("day").valueOf();
    const endtime = moment(this.state.selectedDay).endOf("day").valueOf();
    const vehicleIds = selected.map((v) => parseInt(v.id, 10));

    this.setState({
      busy: true,
      vehicles: [],
      errorMsg: null,
      expandedVehicleId: null,
      expandedDay: {},
    });

    ReportAPIs.getEnergyConsumeReport({
      vehicleid: vehicleIds,
      starttime,
      endtime,
    }).then(
      (resp) => {
        if (this.unmounted) return;

        const expandedDay = {};
        const vehicles = sortVehiclesByData(
          selected.map((item) => {
            const vehicleid = parseInt(item.id, 10);
            const key = String(vehicleid);
            const days =
              (resp && (resp[key] || resp[vehicleid])) || [];
            const first = days[0] || {};

            if (days.length && expandedDay[vehicleid] === undefined) {
              expandedDay[vehicleid] = 0;
            }

            return {
              vehicleid,
              vehicleno: this.resolveVehicleName(item, days),
              status: first.status || null,
              days,
            };
          })
        );

        const firstWithData = vehicles.find(vehicleHasData);
        const expandedVehicleId = firstWithData
          ? firstWithData.vehicleid
          : null;

        const hasAnyData = vehicles.some(vehicleHasData);
        this.setState({
          busy: false,
          vehicles,
          expandedVehicleId,
          expandedDay,
          errorMsg: hasAnyData ? null : "No data for selected day",
        });
      },
      () => {
        if (this.unmounted) return;
        this.setState({
          busy: false,
          vehicles: [],
          expandedVehicleId: null,
          expandedDay: {},
          errorMsg: "Data fetching failed",
        });
      }
    );
  };

  toggleVehicle = (vehicleid) => {
    this.setState((prev) => ({
      expandedVehicleId:
        prev.expandedVehicleId === vehicleid ? null : vehicleid,
    }));
  };

  toggleDay = (vehicleid, index) => {
    this.setState((prev) => ({
      expandedDay: {
        ...prev.expandedDay,
        [vehicleid]:
          prev.expandedDay[vehicleid] === index ? null : index,
      },
    }));
  };

  buildMainRows = (vehicles) => {
    const rows = [];

    vehicles.forEach((vehicle) => {
      const days = vehicle.days || [];
      if (!days.length) {
        rows.push({
          "Vehicle No": vehicle.vehicleno,
          Day: "-",
          "Total Energy (kWh)": "-",
          Status: vehicle.status ? formatStatus(vehicle.status) : "-",
        });
        return;
      }

      days.forEach((day) => {
        rows.push({
          "Vehicle No": day.vehicleno || vehicle.vehicleno,
          Day: moment(day.day).format("DD MMM YYYY"),
          "Total Energy (kWh)":
            day.totalenergyconsumed != null ? day.totalenergyconsumed : "-",
          Status: day.status ? formatStatus(day.status) : "-",
        });
      });
    });

    return rows;
  };

  buildSplitRows = (vehicles) => {
    const rows = [];

    vehicles.forEach((vehicle) => {
      const days = vehicle.days || [];
      days.forEach((day) => {
        const splits = day.split || [];
        splits.forEach((s, i) => {
          rows.push({
            "Vehicle No": day.vehicleno || vehicle.vehicleno,
            Day: moment(day.day).format("DD MMM YYYY"),
            Session: i + 1,
            "Start Time": moment(s.starttime).format("DD-MMM-YYYY HH:mm"),
            "End Time": moment(s.endtime).format("DD-MMM-YYYY HH:mm"),
            "Start SOC": s.startsoc != null ? s.startsoc : "-",
            "End SOC": s.endsoc != null ? s.endsoc : "-",
            "Energy (kWh)": s.energyconsumed != null ? s.energyconsumed : "-",
          });
        });
      });
    });

    return rows;
  };

  downloadMainData = () => {
    const rows = this.buildMainRows(this.state.vehicles);
    if (!rows.length) {
      this.setState({ errorMsg: "No data Available", downloadMenuOpen: false });
      return;
    }
    JSONToCSVConvertor(rows, "energy_consume_report_summary", false);
    this.setState({ downloadMenuOpen: false });
  };

  downloadSplitData = () => {
    const rows = this.buildSplitRows(this.state.vehicles);
    if (!rows.length) {
      this.setState({
        errorMsg: "No split data Available",
        downloadMenuOpen: false,
      });
      return;
    }
    JSONToCSVConvertor(rows, "energy_consume_report_events", false);
    this.setState({ downloadMenuOpen: false });
  };

  handleDownloadClick = () => {
    this.setState((prev) => ({ downloadMenuOpen: !prev.downloadMenuOpen }));
  };

  handleDownloadOutsideClick = (e) => {
    if (
      this.downloadWrapRef.current &&
      !this.downloadWrapRef.current.contains(e.target)
    ) {
      this.setState({ downloadMenuOpen: false });
    }
  };

  renderSession = (session, index) => (
    <div className="ecr-session" key={index}>
      <div className="ecr-session-top">
        <div className="ecr-session-title">Session {index + 1}</div>
        <div className="ecr-session-energy">
          {session.energyconsumed != null ? session.energyconsumed : "--"} kWh
        </div>
      </div>
      <div className="ecr-session-meta">
        {moment(session.starttime).format("hh:mm A")} -{" "}
        {moment(session.endtime).format("hh:mm A")}
        <span className="ecr-dot">·</span>
        {formatDuration(session.starttime, session.endtime)}
      </div>
      <div className="ecr-soc">
        <div className="ecr-soc-item">
          <span className="ecr-soc-label">Start SOC</span>
          <span className="ecr-soc-value">
            {session.startsoc != null ? `${session.startsoc}%` : "--"}
          </span>
        </div>
        <div className="ecr-soc-item">
          <span className="ecr-soc-label">End SOC</span>
          <span className="ecr-soc-value">
            {session.endsoc != null ? `${session.endsoc}%` : "--"}
          </span>
        </div>
      </div>
    </div>
  );

  renderDay = (vehicle, day, index) => {
    const expanded = this.state.expandedDay[vehicle.vehicleid] === index;
    const splits = day.split || [];
    return (
      <div
        className={`ecr-day ${expanded ? "is-expanded" : ""}`}
        key={day.day || index}
      >
        <div
          className="ecr-day-header"
          onClick={() => this.toggleDay(vehicle.vehicleid, index)}
        >
          <div className="ecr-day-left">
            <i className="fa fa-calendar" aria-hidden="true" />
            <div>
              <div className="ecr-day-date">
                {moment(day.day).format("DD MMM YYYY")}
              </div>
              <div className="ecr-day-weekday">
                {moment(day.day).format("dddd")}
              </div>
            </div>
          </div>
          <div className="ecr-day-right">
            <div
              className="ecr-status-badge"
              data-tooltip={getStatusTooltip(day.status)}
            >
              {formatStatus(day.status)}
            </div>
            <div className="ecr-day-stat">
              <div className="ecr-day-stat-label">Sessions</div>
              <div className="ecr-day-stat-count">{splits.length}</div>
            </div>
            <i
              className={`fa fa-chevron-${expanded ? "up" : "down"}`}
              aria-hidden="true"
            />
          </div>
        </div>
        {expanded ? (
          <div className="ecr-sessions">
            <div className="ecr-sessions-title">Driving Sessions</div>
            {splits.length ? (
              <div className="ecr-session-grid">
                {splits.map((s, i) => this.renderSession(s, i))}
              </div>
            ) : (
              <div className="ecr-empty-sessions">No sessions</div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  renderVehicleCard = (vehicle) => {
    const expanded = this.state.expandedVehicleId === vehicle.vehicleid;
    const totalEnergy = (vehicle.days || []).reduce((sum, day) => {
      const value = day.totalenergyconsumed;
      return sum + (value != null ? Number(value) : 0);
    }, 0);
    const hasEnergy = (vehicle.days || []).some(
      (day) => day.totalenergyconsumed != null
    );

    return (
      <div
        className={`ecr-vehicle-card ${expanded ? "is-expanded" : ""}`}
        key={vehicle.vehicleid}
      >
        <div
          className="ecr-vehicle-card-header"
          onClick={() => this.toggleVehicle(vehicle.vehicleid)}
        >
          <div className="ecr-vehicle-name">{vehicle.vehicleno}</div>
          <div className="ecr-vehicle-card-actions">
            <div className="ecr-vehicle-energy">
              <div className="ecr-vehicle-energy-label">
                Total Energy Consumed
              </div>
              <div className="ecr-vehicle-energy-value">
                {hasEnergy ? totalEnergy : "--"} kWh
              </div>
            </div>
            <i
              className={`fa fa-chevron-${expanded ? "up" : "down"}`}
              aria-hidden="true"
            />
          </div>
        </div>
        {expanded ? (
          <div className="ecr-days">
            {vehicle.days.length ? (
              vehicle.days.map((day, index) =>
                this.renderDay(vehicle, day, index)
              )
            ) : (
              <div className="ecr-empty-sessions">
                No data for selected day
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  componentDidMount() {
    window.$(".rw-container").css("top", "53px");
    document.addEventListener("click", this.handleDownloadOutsideClick);
  }

  componentWillUnmount() {
    this.unmounted = true;
    document.removeEventListener("click", this.handleDownloadOutsideClick);
    window.$(".rw-container").attr("style", function (i, style) {
      return style && style.replace(/top[^;]+;?/g, "");
    });
  }

  render() {
    const { busy, vehicles, errorMsg, selectedDay } = this.state;
    const hasResults = vehicles.length > 0;

    return (
      <div className="EnergyConsumeReport">
        <div className="ecr-filters">
          <div className="ecr-field">
            <label>Vehicles</label>
            <div className="ecr-vehicle">
              <InMultiSelect
                ref={(ref) => {
                  this.vehicleSelect = ref;
                }}
                data={{
                  storeData: (state) => {
                    return {
                      data: (() => {
                        let newMap = {};
                        for (let idx in state.vehicleMap) {
                          if (idx.indexOf("user") === -1) {
                            newMap[idx] = state.vehicleMap[idx];
                          }
                        }
                        return newMap;
                      })(),
                      name: "vehicleno",
                      id: "vehicleid",
                    };
                  },
                  key: "vehicle",
                  onItemChange: this.changeVehicle.bind(this),
                  schemaKey: "energy_consume_report",
                  defaultValue: () => {
                    return {
                      data: (window.selectedVehicles || []).map((i) => i.id),
                      updatedAt: window.selectedVehiclesUpdatedAt,
                    };
                  },
                }}
              />
            </div>
          </div>

          <div className="ecr-field">
            <label>Day</label>
            <div className="ecr-day-picker">
              <i className="fa fa-calendar" aria-hidden="true" />
              <DatePicker
                wrapperClassName="ecr-datepicker"
                selected={selectedDay}
                onChange={this.handleDayChange}
                dateFormat="dd MMM yyyy"
              />
            </div>
          </div>

          <button className="ecr-get-btn" onClick={this.getReport} type="button">
            Get Report
          </button>

          <div className="ecr-download-wrap" ref={this.downloadWrapRef}>
            <button
              className="ecr-download-btn"
              onClick={this.handleDownloadClick}
              type="button"
              title="Download"
            >
              <i className="fa fa-download" aria-hidden="true" />
            </button>
            {this.state.downloadMenuOpen ? (
              <div className="ecr-download-menu">
                <button
                  className="ecr-download-option"
                  onClick={this.downloadMainData}
                  type="button"
                >
                  Download Summary
                </button>
                <button
                  className="ecr-download-option"
                  onClick={this.downloadSplitData}
                  type="button"
                >
                  Download Events
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="ecr-body">
          {busy ? (
            <div className="ecr-busy">
              <div className="ecr-loader" />
            </div>
          ) : hasResults ? (
            <div className="ecr-vehicle-list">
              {vehicles.map((vehicle) => this.renderVehicleCard(vehicle))}
            </div>
          ) : (
            <div className="ecr-placeholder">
              {errorMsg ||
                "Select vehicles and day, then click Get Report"}
            </div>
          )}
        </div>

        <div className="ecr-footer">
          <i className="fa fa-info-circle" aria-hidden="true" />
          All energy values are in kWh. Time shown in local time.
        </div>
      </div>
    );
  }
}

export default EnergyConsumeReport;
